import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  X,
  ShoppingBag,
  ExternalLink,
  Check,
  Truck,
} from "lucide-react";
import { Product } from "../../data/catalog";
import { WHATSAPP_NUMBER } from "../../config/constants";
import { DeliveryOption } from "../../data/deliveryConfig";
import { useDeliveryOptions } from "../../hooks/useDeliveryOptions";
import DeliveryZoneSelector from "../ui/DeliveryZoneSelector";

interface OrderWhatsAppModalProps {
  product: Product & { images?: string[] };
  priceOption: { people: string; price: number };
  isOpen: boolean;
  onClose: () => void;
}

const OrderWhatsAppModal: React.FC<OrderWhatsAppModalProps> = ({
  product,
  priceOption,
  isOpen,
  onClose,
}) => {
  const deliveryOptions = useDeliveryOptions();
  const [selectedDeliveryId, setSelectedDeliveryId] = useState("pickup");
  const [selectedDeliveryPrice, setSelectedDeliveryPrice] = useState(0);
  const selectedDelivery =
    deliveryOptions.find((o) => o.id === selectedDeliveryId) || deliveryOptions[0];

  const handleDeliveryChange = (opt: DeliveryOption, price: number) => {
    setSelectedDeliveryId(opt.id);
    setSelectedDeliveryPrice(opt.type === "pickup" ? 0 : price);
  };

  useEffect(() => {
    setSelectedDeliveryId("pickup");
    setSelectedDeliveryPrice(0);
  }, [product?.id, priceOption?.people, priceOption?.price]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const deliveryInfo =
    selectedDelivery.type === "pickup"
      ? `Pick Up en ${selectedDelivery.address}`
      : `Delivery ${selectedDelivery.zone}`;

  const totalPrice = priceOption.price + (selectedDelivery.type === "delivery" ? selectedDeliveryPrice : 0);

  const whatsappMessage = encodeURIComponent(
    `¡Hola Estef's Cakes! 🍰\n\nMe gustaría pedir este postre:\n*${product.name}*\n• Tamaño/Porción: ${priceOption.people}\n• Precio producto: ${priceOption.price} €${selectedDelivery.type === "delivery" ? `\n• Delivery: ${selectedDelivery.zone} ${selectedDeliveryPrice} €` : ""}\n• Total: ${totalPrice} €\n• Zona de entrega: ${deliveryInfo}\n\n¿Tienen disponibilidad?`
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-rose-950/40 backdrop-blur-md animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-rose-100/80 overflow-hidden animate-scaleUp max-h-[92vh] flex flex-col my-auto"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-rose-100/60 bg-gradient-to-r from-rose-50/70 via-white to-pink-50/50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xl">{product.emoji}</span>
            <span className="font-body text-xs font-semibold text-rose-600 uppercase tracking-wider">
              Confirmar Pedido
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-rose-100/80 hover:bg-rose-200 text-rose-800 flex items-center justify-center transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-center justify-between bg-rose-50/60 p-3 rounded-2xl border border-rose-100">
            <div className="min-w-0">
              <p className="font-display text-sm font-bold text-rose-950 truncate">
                {product.name}
              </p>
              <p className="text-[11px] text-gray-500">
                {priceOption.people}
                {selectedDelivery.type === "delivery" && (
                  <span className="text-[9px] text-rose-500">
                    {" "}
                    + delivery (Ref {selectedDeliveryPrice})
                  </span>
                )}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="font-display text-lg font-black text-rose-700">
                <span className="text-[10px] font-normal text-rose-500">Ref</span>{" "}
                {totalPrice}
              </span>
            </div>
          </div>

          <DeliveryZoneSelector
            options={deliveryOptions}
            selectedId={selectedDelivery?.id || "pickup"}
            selectedPrice={selectedDeliveryPrice}
            onChange={handleDeliveryChange}
          />

          {/* Info summary */}
          <div className="flex items-center gap-2 bg-emerald-50/60 p-3 rounded-2xl border border-emerald-100">
            <Truck className="w-4 h-4 text-emerald-700 flex-shrink-0" />
            <p className="font-body text-xs font-medium text-emerald-900">
              {selectedDelivery.type === "pickup"
                ? `Recogida en ${selectedDelivery.address} — sin costo adicional.`
                : `Envío a ${selectedDelivery.zone}: +Ref ${selectedDeliveryPrice} al total del pedido.`}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-2 border-t border-rose-100/60 space-y-2 flex-shrink-0">
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
            target="_blank"
            rel="noreferrer"
            className="w-full py-3 px-5 bg-gradient-to-r from-rose-700 to-rose-800 hover:from-rose-800 hover:to-rose-900 text-white rounded-xl font-body font-bold text-sm shadow-md hover:shadow-lg active:scale-[0.99] transition-all flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" />
            <span>Enviar Pedido por WhatsApp</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="w-full flex items-center justify-center gap-1.5 text-[11px] font-medium text-rose-600/80 hover:text-rose-800 py-1.5 transition-colors"
          >
            <Check className="w-3 h-3" />
            Continuar viendo el catálogo
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrderWhatsAppModal;